import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { environment } from 'src/environments/environment';
import { sinInterceptorGlobal } from 'src/app/shared/utils/error-http-real';
import type { EstadoTramosUsdtResponse } from './main-rates.service';
import {
  CamposDistancia,
  FamiliaDistancia,
  familiaUrl,
  FilaDistancia,
  filasParaEnviar,
  HistorialDistanciaPagina,
  RutaUsdt,
  TablaDistanciaResponse,
} from '../components/main-rates/tramos-distancia.rules';

/** Estado de una ruta (`GET rates/tramos-usdt/{ruta}`) con los campos aditivos del modelo de distancia. */
export type EstadoRutaDistancia = EstadoTramosUsdtResponse & CamposDistancia;

/**
 * Tabla de distancia de los tramos USDT (modelo de Oscar, 23/10/2026).
 *
 * Todas las llamadas van sin el `ErrorInterceptor` global: el editor muestra
 * sus propios mensajes ante 403/404/409 y el bloque del panel, que lee cada
 * minuto, no debe abrir el aviso global. Los errores salen crudos
 * (`HttpErrorResponse`), que `statusDe` / `erroresDe` ya entienden.
 *
 * Con la API vieja estos endpoints dan 404: las lecturas de fondo devuelven
 * null y el panel se queda como siempre.
 */
@Injectable({ providedIn: 'root' })
export class TramosDistanciaService {
  private apiUrl = environment.apiNest;

  /** Lecturas de fondo compartidas por los bloques que piden lo mismo a la vez. */
  static readonly VENTANA_COMPARTIDA_MS = 20_000;
  private compartidos = new Map<string, { en: number; obs: Observable<any> }>();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  private async headers(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    const token = session?.accessToken?.jwtToken?.trim();
    if (!token) throw new Error('No se pudo obtener el token de sesión');
    return sinInterceptorGlobal(new HttpHeaders().set('Authorization', `Bearer ${token}`));
  }

  private compartido<T>(clave: string, crear: () => Observable<T>): Observable<T> {
    const ahora = Date.now();
    const previo = this.compartidos.get(clave);
    if (previo && ahora - previo.en < TramosDistanciaService.VENTANA_COMPARTIDA_MS) {
      return previo.obs as Observable<T>;
    }
    const obs = crear().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.compartidos.set(clave, { en: ahora, obs });
    return obs;
  }

  /** Tras guardar: la próxima lectura va al servidor. */
  olvidar(): void {
    this.compartidos.clear();
  }

  /**
   * Estado de la ruta tal como lo cobra el API, con `modelo`, `referencia`,
   * `chica`, `grande`, `margenPct`… cuando el API ya los manda. Nunca lanza:
   * null si falla. GET rates/tramos-usdt/{ruta}
   */
  obtenerEstadoRuta(ruta: RutaUsdt): Observable<EstadoRutaDistancia | null> {
    const seg = ruta.toLowerCase();
    return this.compartido(`estado-${seg}`, () =>
      from(this.headers()).pipe(
        switchMap((headers) =>
          this.http.get<EstadoRutaDistancia>(`${this.apiUrl}rates/tramos-usdt/${seg}`, { headers }),
        ),
        map((r) => (r && typeof r === 'object' ? r : null)),
        catchError(() => of(null)),
      ),
    );
  }

  /**
   * La tabla de la familia para el bloque del panel (de fondo: null si falla
   * o si el API todavía no la tiene). GET rates/tramos-distancia/:familia
   */
  leerTabla(familia: FamiliaDistancia): Observable<TablaDistanciaResponse | null> {
    return this.compartido(`tabla-${familia}`, () =>
      this.obtenerTabla(familia).pipe(catchError(() => of(null))),
    );
  }

  /** Para el editor: lanza el error crudo (404, 403…). GET rates/tramos-distancia/:familia */
  obtenerTabla(familia: FamiliaDistancia): Observable<TablaDistanciaResponse> {
    return from(this.headers()).pipe(
      switchMap((headers) =>
        this.http.get<TablaDistanciaResponse>(`${this.apiUrl}rates/tramos-distancia/${familiaUrl(familia)}`, {
          headers,
        }),
      ),
    );
  }

  /** POST rates/tramos-distancia/:familia/previsualizar { filas } */
  previsualizar(familia: FamiliaDistancia, filas: FilaDistancia[]): Observable<TablaDistanciaResponse> {
    return from(this.headers()).pipe(
      switchMap((headers) =>
        this.http.post<TablaDistanciaResponse>(
          `${this.apiUrl}rates/tramos-distancia/${familiaUrl(familia)}/previsualizar`,
          { filas: filasParaEnviar(filas) },
          { headers },
        ),
      ),
    );
  }

  /**
   * PUT rates/tramos-distancia/:familia { filas, motivo, version }. 409 si
   * otro operador guardó antes; 403 si el correo no puede editar (prod).
   */
  guardar(
    familia: FamiliaDistancia,
    filas: FilaDistancia[],
    motivo: string,
    version: string | number | null,
  ): Observable<TablaDistanciaResponse> {
    const cuerpo: Record<string, unknown> = { filas: filasParaEnviar(filas), motivo };
    if (version != null && version !== '') cuerpo['version'] = version;
    return from(this.headers()).pipe(
      switchMap((headers) =>
        this.http.put<TablaDistanciaResponse>(`${this.apiUrl}rates/tramos-distancia/${familiaUrl(familia)}`, cuerpo, {
          headers,
        }),
      ),
    );
  }

  /** GET rates/tramos-distancia/:familia/historial?pagina&tamano */
  historial(familia: FamiliaDistancia, pagina: number, tamano: number): Observable<HistorialDistanciaPagina> {
    return from(this.headers()).pipe(
      switchMap((headers) =>
        this.http.get<HistorialDistanciaPagina>(
          `${this.apiUrl}rates/tramos-distancia/${familiaUrl(familia)}/historial`,
          { headers, params: { pagina: String(pagina), tamano: String(tamano) } },
        ),
      ),
    );
  }
}
